/* eslint-disable react-perf/jsx-no-new-function-as-prop */
import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  InputSt,
  MessageSt,
  SignupFormSt,
} from 'app/shared/components/NewsletterSignupForm/NewsletterSignupForm.css';
import { InputSubmitBox } from 'app/shared/components/InputSubmitBox/InputSubmitBox';
import { handleEmailValidation } from 'app/marketplace/accounts/helpers/handleValidators';
import { isNotEmpty, isValidEmail } from 'app/shared/utils/validators';
import { noop } from 'app/shared/utils/utils';
import { INVALID_EMAIL_ERROR_MSG } from 'app/shared/utils/errorMessages';

export const messages = {
  success:
    'Wir haben dir eine E-Mail geschickt. Bitte bestätige deine E-Mail-Adresse!',
  error: 'Bitte gib eine gültige E-Mail Adresse ein.',
  used: 'E-Mail-Adresse wurde bereits registriert.',
};

/**
 * Renders a Newsletter input
 * @param onFormSubmit
 * @param onInputFocus
 * @param placeholder
 * @param maxWidth
 * @returns {*}
 * @constructor
 */
export const NewsletterSignupForm = ({
  onFormSubmit,
  onSuccess,
  onInputFocus,
  placeholder,
  maxWidth,
  trackFocus,
  trackSubmit,
  alignMessage,
  source,
}) => {
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [message, setMessage] = useState(null);
  const [formStatus, setFormStatus] = useState(null);

  const onFocus = () => {
    trackFocus();
    onInputFocus();
  };

  const onChange = (e) => {
    setEmail(e.target.value);
  };

  const updateStatus = (status, newMessage) => {
    setFormStatus(status);
    setMessage(newMessage);
  };

  const onBlur = (e) => {
    const value = e?.target?.value;
    const validated = isValidEmail(value) && isNotEmpty(value);
    setIsValid(validated);
    if (!isNotEmpty(value)) {
      updateStatus('error', INVALID_EMAIL_ERROR_MSG);
    } else if (isNotEmpty && !isValidEmail(value)) {
      updateStatus('error', handleEmailValidation(value));
    } else updateStatus();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isValid) {
      trackSubmit().init();

      onFormSubmit({ email, source })
        .then(() => {
          onSuccess();
          trackSubmit().success();
          return updateStatus(
            'success',
            'Wir haben dir eine E-Mail geschickt. Bitte bestätige deine E-Mail-Adresse!',
          );
        })
        .catch((error) => {
          trackSubmit().fail();
          // handle case for existing user email id
          if (error?.status === 409) {
            return updateStatus('error', 'Der Benutzer existiert bereits.');
          }
          return updateStatus(
            'error',
            error?.status === 406
              ? 'E-Mail-Adresse wurde bereits registriert.'
              : 'Bitte gib eine gültige E-Mail Adresse ein.',
          );
        });
    } else {
      trackSubmit().fail();
      return updateStatus(
        'error',
        'Bitte gib eine gültige E-Mail Adresse ein.',
      );
    }
  };

  const status = !isValid ? 'error' : formStatus;

  return (
    <SignupFormSt>
      <form onSubmit={onSubmit} noValidate>
        <InputSubmitBox status={status}>
          <InputSt
            name="email"
            type="email"
            autoComplete="email"
            id="newsletter-email-input"
            aria-label="E-Mail-Adresse eingeben"
            required
            maxWidth={maxWidth}
            label="Bitte gib eine gültige E-Mail Adresse ein"
            placeholder={placeholder}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            value={email}
          />
        </InputSubmitBox>
        <MessageSt status={formStatus} align={alignMessage}>
          {message}
        </MessageSt>
      </form>
    </SignupFormSt>
  );
};

NewsletterSignupForm.propTypes = {
  /** Function to run on form submit */
  onFormSubmit: PropTypes.func,
  /** Function to run on form submit */
  onSuccess: PropTypes.func,
  /** Optional function to run on input focus */
  onInputFocus: PropTypes.func,
  /** Input placeholder */
  placeholder: PropTypes.string,
  /** Max width; it takes 100% of parent otherwise */
  maxWidth: PropTypes.number,
  /** Function to track user focusing on input */
  trackFocus: PropTypes.func,
  /** Function to track submitting the form */
  trackSubmit: PropTypes.func,
  /** The message alignment */
  alignMessage: PropTypes.string,
  /** The value of subscription type */
  source: PropTypes.string,
};

NewsletterSignupForm.defaultProps = {
  onFormSubmit: noop,
  onSuccess: noop,
  onInputFocus: noop,
  placeholder: 'Deine E-Mail',
  maxWidth: null,
  trackFocus: noop,
  trackSubmit: () => ({
    init: noop,
    success: noop,
    fail: noop,
  }),
  alignMessage: 'left',
  source: null,
};
